import {
	Box,
	type CircularProgressProps,
	keyframes,
	useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { LOADING_BOX, LOADING_SHAPES, STILL_SHAPE } from './loading-shapes'

export type LoadingProps = {
	size?: number
	color?: CircularProgressProps['color']
	variant?: 'overlay' | 'inline' | 'page'
	contained?: boolean
	backdrop?: boolean
	delay?: number
	label?: string
}

const MORPH_STEP_SECONDS = 0.65
const SPLINE = '0.4 0 0.2 1'

const enter = keyframes`
	from { opacity: 0; transform: scale(0.9); }
	to { opacity: 1; transform: scale(1); }
`
const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`
const spin = keyframes`
	to { transform: rotate(360deg); }
`
const pulse = keyframes`
	from { opacity: 1; }
	to { opacity: 0.5; }
`

const layout = {
	overlay: { position: 'absolute', inset: 0, zIndex: 1 },
	page: { position: 'fixed', inset: 0, zIndex: 'modal' },
	inline: { display: 'inline-flex', verticalAlign: 'middle' },
} as const

function Loading({
	size = 120,
	color = 'primary',
	variant = 'overlay',
	contained = false,
	backdrop = false,
	delay = 300,
	label = 'Loading…',
}: LoadingProps) {
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
		noSsr: true,
	})

	return (
		<Box
			role="status"
			aria-busy="true"
			aria-live="polite"
			sx={[
				{ display: 'grid', placeContent: 'center' },
				layout[variant],
				backdrop && variant !== 'inline'
					? {
							backgroundColor: (theme) =>
								alpha(theme.palette.background.paper, 0.6),
							backdropFilter: 'blur(2px)',
						}
					: {},
			]}
		>
			<Box
				sx={{
					display: 'flex',
					color: color === 'inherit' ? 'inherit' : `${color}.main`,
					animation: `${reduceMotion ? fadeIn : enter} 200ms ease-out ${delay}ms both`,
				}}
			>
				<svg
					role="progressbar"
					aria-label={label}
					width={size}
					height={size}
					viewBox={`0 0 ${LOADING_BOX} ${LOADING_BOX}`}
				>
					{contained && (
						<circle
							cx={LOADING_BOX / 2}
							cy={LOADING_BOX / 2}
							r={LOADING_BOX / 2}
							fill="currentColor"
							fillOpacity={0.16}
						/>
					)}
					<Box
						component="path"
						d={STILL_SHAPE}
						fill="currentColor"
						sx={{
							transformOrigin: 'center',
							transformBox: 'view-box',
							animation: reduceMotion
								? `${pulse} 1.5s ease-in-out infinite alternate`
								: `${spin} 4s linear infinite`,
						}}
					>
						{!reduceMotion && (
							<animate
								attributeName="d"
								values={LOADING_SHAPES.join(';')}
								dur={`${MORPH_STEP_SECONDS * (LOADING_SHAPES.length - 1)}s`}
								repeatCount="indefinite"
								calcMode="spline"
								keySplines={Array(LOADING_SHAPES.length - 1)
									.fill(SPLINE)
									.join(';')}
							/>
						)}
					</Box>
				</svg>
			</Box>
		</Box>
	)
}

export { Loading, Loading as default }
